import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Basket, Timer, Storefront, Truck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PantryPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/pantry/public").then(({ data }) => setBoard((b) => ({ ...(b || {}), ...data })))
      .catch(() => setBoard((b) => ({ stock: [], ...(b || {}) })));
    api.get("/storage/public").then(({ data }) => setBoard((b) => ({
      ...(b || { stock: [] }), spaces: data.spaces, spacePrices: data.prices,
      spaceBrands: data.brands, spaceDay: data.day, spaceImage: data.image_url,
      spaceListed: data.listed, spaceCheapest: data.cheapest,
      spaceVanDay: data.van_day_base, spaceSymbol: data.symbol,
      spacePerSqft: data.per_sqft_month,
      spaceShare: data.share_token, spacePosted: data.auto_posted,
      spaceSizes: data.sizes, spaceVehicles: data.vehicles,
    }))).catch(() => {});
  }, []);

  useSeo({
    title: "Indian groceries delivered in remote areas — in the hub, home in half an hour | Unity & Utility",
    description: "The Indian grocery stores are 250 miles away. Order flour, oil, ghee, dal, rice and fresh vegetables, we run it out twice a week to a local hub, and it is home in about thirty minutes.",
    path: "/pantry",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Remote pantry", "/pantry"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="pantry-public-page">
        <Crumbs items={[["Home", "/"], ["Remote pantry", "/pantry"]]} />
        <p className="overline text-[#B3241B]">Remote pantry</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Basket size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          The Indian grocery shop, 250 miles closer
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="pantry-public-intro">
          Atta and maida, mustard oil and ghee, toor and moong dal, basmati, okra, curry leaves and
          coriander — the stores that carry them are four hours away. So we buy them there, run them
          out twice a week to a hub a few miles from home, and bring them to your door in about{" "}
          {board?.promise_minutes || 30} minutes once they land.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token ? `${window.location.origin}/p/${board.share_token}` : `${window.location.origin}/pantry`}
            text="Indian groceries out in the sticks — ordered from the big stores, in the local hub, home in half an hour."
            testid="pantry-public-share" instagram />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt="Indian grocery staples stacked at the local pickup hub"
            loading="lazy" className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="pantry-public-image" />
        )}

        {board?.hub && (
          <section className="mt-8 border border-[#002FA7] bg-white p-5" data-testid="pantry-public-hub">
            <p className="overline text-[#002FA7]">Your hub{board.day ? ` · ${board.day}` : ""}
              {board.auto_posted && <span className="ml-2 text-[#0B5C2E]">Posted to Facebook</span>}
            </p>
            <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              {board.hub.name}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {board.hub.where} · {board.hub.miles_from_homes} miles from home ·
              runs {board.hub.cadence}{(board.hub.run_days || []).length ? ` (${board.hub.run_days.join(" and ")})` : ""}
            </p>
            <p className="mt-2 text-sm font-semibold text-[#0B5C2E]">
              <Timer size={14} className="mr-1 inline align-[-2px]" />
              Anything in the hub is with you in about {board.promise_minutes} minutes
            </p>
          </section>
        )}

        {(board?.offers || []).length > 0 && (
          <section className="mt-8 border border-[#B3241B] bg-[#FFF5F4] p-5" data-testid="pantry-public-offers">
            <p className="overline text-[#B3241B]">Special deals</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {board.offers.map((o) => (
                <li key={o.id} data-testid={`pantry-public-offer-${o.id}`}>
                  <span className="font-semibold text-[#0F172A]">{o.title}</span>
                  {o.discount_pct ? ` — ${o.discount_pct}% off` : ""}{o.store ? ` at ${o.store}` : " on everything"}
                </li>
              ))}
            </ul>
          </section>
        )}

        {(board?.zones || []).length > 0 && (
          <section className="mt-8" data-testid="pantry-public-zones">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              What delivery costs, country by country
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.zones.map((z) => (
                <div key={z.country} className="border border-border bg-white p-4"
                  data-testid={`pantry-public-zone-${(z.currency || "x")}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{z.country}</p>
                  <p className="text-xs text-slate-500">
                    Delivery from {z.symbol}{Number(z.delivery_base || 0).toFixed(2)}
                    {z.free_delivery_over ? ` · free over ${z.symbol}${Number(z.free_delivery_over).toFixed(0)}` : ""}
                  </p>
                  <p className="overline mt-1 text-[0.6rem] text-slate-400">{(z.currency || "").toUpperCase()}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Every charge — delivery, hub handling, service and any runner tip — is itemised before you pay.
            </p>
          </section>
        )}

        <h2 className="font-display mt-10 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
          In the hub right now
        </h2>
        {(board?.stock || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-600" data-testid="pantry-public-empty">
            The hub is between runs. Order anything you like and it comes on the next one.
          </p>
        ) : (
          <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="pantry-public-stock">
            {board.stock.map((s) => (
              <div key={s.id} className="border border-border bg-white p-4" data-testid={`pantry-public-item-${s.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{s.item}</p>
                <p className="text-xs text-slate-500">{s.pack} · {s.qty} in · ${Number(s.price || 0).toFixed(2)}</p>
                <p className="overline mt-1 text-[0.6rem] text-slate-400">{s.category}</p>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display mt-10 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
          Where it comes from
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="pantry-public-stores">
          {(board?.stores || []).map((s) => (
            <div key={s.id} className="border border-border bg-white p-4" data-testid={`pantry-store-${s.id}`}>
              <p className="text-sm font-semibold text-[#0F172A]">
                <Storefront size={14} className="mr-1 inline align-[-2px] text-[#002FA7]" />{s.name}
              </p>
              <p className="text-xs text-slate-500">{s.city} · {s.miles} miles · about {s.hours} hours each way</p>
              {s.note && <p className="mt-1 text-sm text-slate-600">{s.note}</p>}
            </div>
          ))}
        </div>

        {(board?.spaceDay || (board?.spaces || []).length > 0) && (
          <section className="mt-8" data-testid="pantry-public-space">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              Space near you, and a van to move it
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
              A self-storage unit, a spare back room, a barn or a shipping container — plus cargo
              vans and box trucks for the run. Rent it for yourself or for the hub when a load
              lands bigger than the back room.
            </p>

            {board.spaceImage && (
              <img src={`${BACKEND}${board.spaceImage}`}
                alt="Someone carrying boxes out to a hire van beside a storage unit"
                loading="lazy" className="mt-4 h-52 w-full border border-border object-cover sm:h-72"
                data-testid="pantry-public-space-image" />
            )}

            <div className="mt-4 border border-[#0B5C2E] bg-[#F3FAF5] p-4" data-testid="pantry-public-space-card">
              <p className="overline text-[#0B5C2E]">
                Space and vans today{board.spaceDay ? ` · ${board.spaceDay}` : ""}
                {board.spacePosted && <span className="ml-2">Posted to Facebook</span>}
              </p>
              <p className="mt-1 text-sm text-slate-700">
                {(board.spaceListed || (board.spaces || []).length) > 0
                  ? `${board.spaceListed || board.spaces.length} places to put your things.`
                  : "Space is going on the board now — here is what it costs by size."}
                {board.spaceCheapest
                  ? ` Cheapest right now: ${board.spaceCheapest.name} — ${board.spaceCheapest.size} at ${board.spaceCheapest.symbol}${Number(board.spaceCheapest.per_month || 0).toFixed(0)} a month.`
                  : ""}
                {board.spaceVanDay
                  ? ` Moving it yourself? Vans from ${board.spaceSymbol || ""}${Number(board.spaceVanDay).toFixed(2)} a day plus mileage.`
                  : ""}
              </p>
              <div className="mt-3">
                <ShareBar
                  url={board.spaceShare
                    ? `${window.location.origin}/p/${board.spaceShare}`
                    : `${window.location.origin}/pantry`}
                  text="Somewhere to put your things and a van to move it — self storage units, spare back rooms, barns and containers, priced by the month."
                  testid="pantry-public-space-share" instagram />
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="pantry-public-space-list">
              {(board.spaces || []).slice(0, 6).map((s) => (
                <div key={s.id} className="border border-border bg-white p-4"
                  data-testid={`pantry-public-space-${s.id}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                  <p className="text-xs text-slate-500">
                    {s.brand ? `${s.brand} · ` : ""}{s.size} · {s.kind}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {s.symbol}{Number(s.per_month || 0).toFixed(2)}/month
                  </p>
                </div>
              ))}
              {(board.spaces || []).length === 0 && (board.spaceSizes || []).slice(0, 6).map((s) => (
                <div key={s.key} className="border border-border bg-white p-4"
                  data-testid={`pantry-public-size-${s.key}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.holds}</p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {board.spaceSymbol || "$"}
                    {(Number(board.spacePerSqft || 0) * s.sqft).toFixed(2)}/month
                  </p>
                </div>
              ))}
            </div>
            {(board.spaces || []).length === 0 && (
              <p className="mt-2 text-sm text-slate-700" data-testid="pantry-public-space-invite">
                Got a spare back room, garage, barn or container?{" "}
                <Link to="/register" className="font-semibold text-[#002FA7] underline">
                  Put it on the board
                </Link>{" "}
                and let it earn.
              </p>
            )}
            <p className="mt-2 text-xs text-slate-500">
              Van hire from {board.spaceSymbol || "$"}
              {Number(board.spaceVanDay || 0).toFixed(2)} a day plus mileage.
              Named yards and brands we list: {(board.spaceBrands || []).join(", ")}.
            </p>
          </section>
        )}

        <section className="mt-10 border border-border bg-white p-5" data-testid="pantry-public-business">
          <p className="overline text-[#B3241B]">
            <Truck size={13} className="mr-1 inline align-[-2px]" />Run it with us
          </p>
          <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Stores, hub owners and local runners can go online
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            A grocery store that wants the orders, a shop with a spare back room to be the hub, or
            someone local who will run the last few miles and get paid per drop — sign in and apply,
            and you are on the board once it is approved.
          </p>
          <Link to="/register" data-testid="pantry-public-join"
            className="tap-safe mt-3 inline-block bg-[#002FA7] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-[#00248A]">
            Put your business on it
          </Link>
        </section>
      </div>
    </PublicShell>
  );
}
